import { useEffect, useMemo } from 'react';
import { Gallery, ArrowRight2, ArrowLeft2 } from 'iconsax-react';
import { cn } from '@src/design-system/helpers';
import { Button } from '@src/design-system/button';
import { isAnnouncementVideoMime } from '../../utils/upload-announcement-media';
import type { TAnnouncementFormValues } from './schema';

type AnnouncementPreviewProps = {
  values: TAnnouncementFormValues;
};

export function AnnouncementPreview({ values }: AnnouncementPreviewProps) {
  const objectUrl = useMemo(() => {
    if (!values.mediaFile) return null;
    return URL.createObjectURL(values.mediaFile);
  }, [values.mediaFile]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const previewUrl = objectUrl ?? values.existingMedia?.file;
  const mimeType =
    values.mediaFile?.type ?? values.existingMedia?.mime_type ?? '';
  const isVideo =
    values.mediaType === 'video' || isAnnouncementVideoMime(mimeType);

  return (
    <div className='flex h-full flex-col items-center justify-center bg-gray-50 px-6 py-8'>
      <div className='flex w-full max-w-[488px] flex-col items-center gap-2'>
        <div className='flex w-[416px] gap-1'>
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className='h-1 flex-1 rounded-full bg-primary-200'
            >
              <div
                className={cn(
                  'h-full w-[50%] rounded-full bg-gray-800',
                  index !== 0 && 'opacity-0'
                )}
              />
            </div>
          ))}
        </div>

        <div className='flex w-full items-center gap-3'>
          <button
            type='button'
            className='flex size-6 shrink-0 items-center justify-center rounded-full border border-gray-800'
            aria-label='Previous announcement'
          >
            <ArrowLeft2
              size={16}
              className='text-gray-800'
            />
          </button>

          <div className='relative flex max-h-[738px] h-[60vh] w-[416px] shrink-0 flex-col items-center justify-end overflow-hidden rounded-xl bg-gray-200 px-4 py-6'>
            {previewUrl ? (
              isVideo ? (
                <video
                  src={previewUrl}
                  className='absolute inset-0 size-full object-cover'
                  controls
                  playsInline
                />
              ) : (
                <img
                  src={previewUrl}
                  alt='Announcement preview'
                  className='absolute inset-0 size-full object-cover'
                />
              )
            ) : (
              <Gallery
                size={40}
                variant='Outline'
                className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-500'
              />
            )}

            {values.description ? (
              <p className='relative z-10 mb-3 w-full text-center text-sm text-white drop-shadow'>
                {values.description}
              </p>
            ) : null}

            {values.showCta && values.cta_label ? (
              <Button
                type='button'
                className='relative z-10 w-full rounded-xl bg-[#5962ed] hover:bg-[#5962ed]/90'
              >
                {values.cta_label}
                <ArrowRight2
                  size={16}
                  className='ml-1'
                />
              </Button>
            ) : null}
          </div>

          <button
            type='button'
            className='flex size-6 shrink-0 items-center justify-center rounded-full border border-gray-800'
            aria-label='Next announcement'
          >
            <ArrowRight2
              size={16}
              className='text-gray-800'
            />
          </button>
        </div>
      </div>
    </div>
  );
}
